import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PaperPlaneRightIcon,
    PlusIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/Button';
import { SegmentedControl, TextField } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import {
    WebhookAuthorizationForm,
    WebhookAuthorizationType,
    WebhookHeader,
    WebhookTestResult,
} from '@/types/alert';
import {
    authorizationPayload,
    headerNameError,
    isDeliverableWebhookUrl,
    isSecretStored,
    isStoredSecretLeftBehind,
    nonEmptyHeaders,
} from '../validation';
import { formatLatency } from '@/utils/time';

type AuthorizationChoice = 'none' | WebhookAuthorizationType;

const STORED_SECRET_MASK = '••••••••••••';

interface WebhookActionConfigProps {
    alertRuleActionId?: string;
    url: string;
    headers: WebhookHeader[];
    authorization: WebhookAuthorizationForm;
    onUrlChange: (url: string) => void;
    onHeadersChange: (headers: WebhookHeader[]) => void;
    onAuthorizationChange: (authorization: WebhookAuthorizationForm) => void;
}

export function WebhookActionConfig({
    alertRuleActionId,
    url,
    headers,
    authorization,
    onUrlChange,
    onHeadersChange,
    onAuthorizationChange,
}: WebhookActionConfigProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<WebhookTestResult | null>(
        null,
    );

    const urlIsValid = isDeliverableWebhookUrl(url);
    const authorizationOptions: Array<{
        value: AuthorizationChoice;
        label: string;
    }> = [
        { value: 'none', label: t('form.actionConfig.webhooks.authNone') },
        {
            value: WebhookAuthorizationType.BEARER,
            label: t('form.actionConfig.webhooks.authBearer'),
        },
        {
            value: WebhookAuthorizationType.BASIC,
            label: t('form.actionConfig.webhooks.authBasic'),
        },
    ];

    const secretStored = isSecretStored(authorization, url);
    const storedSecretLeftBehind = isStoredSecretLeftBehind(authorization, url);

    const headerNameMessage = (header: WebhookHeader) => {
        const error = headerNameError(header, headers);
        return error
            ? t(`form.actionConfig.webhooks.headerNameErrors.${error}`)
            : undefined;
    };

    const changeAuthorizationType = (choice: AuthorizationChoice) => {
        onAuthorizationChange({
            ...authorization,
            type: choice === 'none' ? null : choice,
            username: '',
            secret: '',
        });
    };

    const updateHeader = (id: string, patch: Partial<WebhookHeader>) => {
        onHeadersChange(
            headers.map((header) =>
                header.id === id ? { ...header, ...patch } : header,
            ),
        );
    };

    const removeHeader = (id: string) => {
        onHeadersChange(headers.filter((header) => header.id !== id));
    };

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            setTestResult(
                await EngineService.testWebhook({
                    url: url.trim(),
                    headers: nonEmptyHeaders(headers),
                    authorization: authorizationPayload(authorization),
                    alertRuleActionId,
                }),
            );
        } catch {
            setTestResult({
                requestId: '',
                latencyMs: 0,
                error: t('form.actionConfig.webhooks.testRequestFailed'),
            });
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="flex flex-col gap-3 pb-3">
            <TextField
                variant="base"
                label={t('form.actionConfig.webhooks.urlLabel')}
                value={url}
                onChange={(event) => onUrlChange(event.target.value)}
                placeholder="https://example.com/hooks/conntour"
                error={
                    url !== '' && !urlIsValid
                        ? t('form.actionConfig.webhooks.invalidUrl')
                        : undefined
                }
                data-testid="webhook-url-input"
            />

            <div className="flex flex-col gap-2">
                <span className="text-sm text-white-100">
                    {t('form.actionConfig.webhooks.headersLabel')}
                </span>
                {headers.map((header) => (
                    <div key={header.id} className="flex items-center gap-2">
                        <TextField
                            variant="base"
                            value={header.name}
                            error={headerNameMessage(header)}
                            onChange={(event) =>
                                updateHeader(header.id, {
                                    name: event.target.value,
                                })
                            }
                            placeholder={t(
                                'form.actionConfig.webhooks.headerNamePlaceholder',
                            )}
                            className="min-w-0 flex-1"
                        />
                        <TextField
                            variant="base"
                            value={header.value}
                            onChange={(event) =>
                                updateHeader(header.id, {
                                    value: event.target.value,
                                })
                            }
                            placeholder={t(
                                'form.actionConfig.webhooks.headerValuePlaceholder',
                            )}
                            className="min-w-0 flex-1"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            shape="square"
                            onClick={() => removeHeader(header.id)}
                            aria-label={t(
                                'form.actionConfig.webhooks.removeHeader',
                            )}
                        >
                            <TrashIcon size={16} />
                        </Button>
                    </div>
                ))}
                <Button
                    variant="ghost"
                    onClick={() =>
                        onHeadersChange([
                            ...headers,
                            { id: crypto.randomUUID(), name: '', value: '' },
                        ])
                    }
                    leadingIcon={<PlusIcon size={16} />}
                    className="w-full justify-start"
                >
                    {t('form.actionConfig.webhooks.addHeader')}
                </Button>
            </div>

            <div className="flex flex-col gap-2">
                <SegmentedControl<AuthorizationChoice>
                    label={t('form.actionConfig.webhooks.authLabel')}
                    labelClassName="text-sm font-normal text-white-100"
                    value={authorization.type ?? 'none'}
                    onValueChange={changeAuthorizationType}
                    options={authorizationOptions}
                    size="compact"
                    fullWidth
                />
                {secretStored && (
                    <div className="flex items-center gap-2">
                        {authorization.type ===
                            WebhookAuthorizationType.BASIC && (
                            <TextField
                                variant="base"
                                value={authorization.username}
                                disabled
                                className="min-w-0 flex-1"
                            />
                        )}
                        <TextField
                            variant="base"
                            value={STORED_SECRET_MASK}
                            disabled
                            className="min-w-0 flex-1"
                            data-testid="webhook-auth-secret-stored"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            shape="square"
                            onClick={() =>
                                onAuthorizationChange({
                                    ...authorization,
                                    username: '',
                                    secret: '',
                                    stored: null,
                                })
                            }
                            aria-label={t(
                                'form.actionConfig.webhooks.resetCredential',
                            )}
                        >
                            <TrashIcon size={16} />
                        </Button>
                    </div>
                )}
                {authorization.type !== null && !secretStored && (
                    <div className="flex items-center gap-2">
                        {authorization.type ===
                            WebhookAuthorizationType.BASIC && (
                            <TextField
                                variant="base"
                                value={authorization.username}
                                onChange={(event) =>
                                    onAuthorizationChange({
                                        ...authorization,
                                        username: event.target.value,
                                    })
                                }
                                placeholder={t(
                                    'form.actionConfig.webhooks.usernameLabel',
                                )}
                                className="min-w-0 flex-1"
                                data-testid="webhook-auth-username"
                            />
                        )}
                        <TextField
                            variant="base"
                            type="password"
                            value={authorization.secret}
                            onChange={(event) =>
                                onAuthorizationChange({
                                    ...authorization,
                                    secret: event.target.value,
                                })
                            }
                            placeholder={
                                authorization.type ===
                                WebhookAuthorizationType.BASIC
                                    ? t(
                                          'form.actionConfig.webhooks.passwordLabel',
                                      )
                                    : t('form.actionConfig.webhooks.tokenLabel')
                            }
                            className="min-w-0 flex-1"
                            data-testid="webhook-auth-secret"
                        />
                    </div>
                )}
                {storedSecretLeftBehind && (
                    <span className="text-xs text-grey-200">
                        {t('form.actionConfig.webhooks.credentialNeedsReentry')}
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <Button
                    variant="secondary"
                    onClick={runTest}
                    disabled={!urlIsValid || testing}
                    leadingIcon={<PaperPlaneRightIcon size={16} />}
                    data-testid="webhook-test-button"
                >
                    {testing
                        ? t('form.actionConfig.webhooks.testing')
                        : t('form.actionConfig.webhooks.test')}
                </Button>
                {testResult && (
                    <div
                        className="flex flex-col gap-1 rounded-[4px] border border-grey-500 bg-grey-700 p-2"
                        data-testid="webhook-test-result"
                    >
                        <span className="text-xs text-white-100">
                            {testResult.statusCode
                                ? t('form.actionConfig.webhooks.testStatus', {
                                      status: testResult.statusCode,
                                      latency: formatLatency(
                                          testResult.latencyMs,
                                      ),
                                  })
                                : t(
                                      'form.actionConfig.webhooks.testNoResponse',
                                  )}
                        </span>
                        {testResult.requestId && (
                            <span className="font-geist-mono text-xs break-all text-grey-300">
                                {t('form.actionConfig.webhooks.requestId', {
                                    requestId: testResult.requestId,
                                })}
                            </span>
                        )}
                        {testResult.error && (
                            <span className="text-xs break-all text-danger-200">
                                {testResult.error}
                            </span>
                        )}
                        {testResult.responseSnippet && (
                            <span className="font-geist-mono text-xs break-all text-grey-300">
                                {testResult.responseSnippet}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
